import React, { useState, useEffect } from 'react';
import { Box, Tabs, Tab, Typography, Grid, Paper, Button, Stack, Divider, CircularProgress, Alert } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import StoreMallDirectoryIcon from '@mui/icons-material/StoreMallDirectory';
import CategoryIcon from '@mui/icons-material/Category';
import InvoiceStatsCard from '../../../components/Cards/InvoiceStatsCard';
import ShopFilter from '../../../components/Filters/ShopFilter';
import CustomerFilter from '../../../components/Filters/CustomerFilter';
import InvoiceTable from '../../../components/Tables/InvoiceTable';
import { exportToExcel } from '../../../utils/exportToExcel';
import { getInvoices, getShops, getCustomers, getApiError } from '../../../utils/api';
import { filterInvoices, filterCustomers, dateMatches, ciMatch, ciIncludes } from '../../../utils/filterUtils';
import { formatMobileNumber } from '../../../utils/formatDate';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box pt={2}>{children}</Box>}
    </div>
  );
}

const SectionHeader = ({ children }) => (
  <Box mb={1} mt={2} display="flex" alignItems="center">
    <Box width={4} height={28} bgcolor="divider" borderRadius={2} mr={1} />
    <Typography variant="h6" fontWeight={700} color="text.primary">{children}</Typography>
  </Box>
);

const Analytics = () => {
  const [tab, setTab] = useState(0);
  const [shopFilters, setShopFilters] = useState({});
  const [customerFilters, setCustomerFilters] = useState({});
  const [invoices, setInvoices] = useState([]);
  const [shops, setShops] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError('');
    Promise.all([
      getInvoices(),
      getShops(),
      getCustomers()
    ])
      .then(([invoicesRes, shopsRes, customersRes]) => {
        if (!mounted) return;
        
        // Create a shops lookup map for efficient filtering
        const shopsMap = new Map();
        const mappedShops = ((shopsRes.data && shopsRes.data.results) || []).map(shop => {
          const mappedShop = {
            ...shop,
            location: shop.city && shop.state ? `${shop.city}, ${shop.state}` : shop.city || shop.state || '',
          };
          shopsMap.set(shop.id, mappedShop);
          return mappedShop;
        });

        // Map invoices with proper shop relationship and location
        const mappedInvoices = ((invoicesRes.data && invoicesRes.data.results) || []).map(inv => {
          const shop = shopsMap.get(inv.shopId);
          return {
            ...inv,
            location: inv.city && inv.state ? `${inv.city}, ${inv.state}` : inv.city || inv.state || '',
            amount: Number(inv.amount) || 0,
            shopName: shop?.name || 'Unknown Shop',
            shopLocation: shop?.location || '',
            // Ensure customer number is properly formatted
            customerNumber: inv.customerNumber,
          };
        });

        // Map customers with proper location handling
        const mappedCustomers = ((customersRes.data && customersRes.data.results) || []).map(cust => ({
          ...cust,
          locationsVisited: Array.isArray(cust.locationsVisited) ? cust.locationsVisited : [],
          // Ensure phone number is properly formatted
          phoneNumber: formatMobileNumber(cust.phoneNumber),
        }));

        setInvoices(mappedInvoices);
        setShops(mappedShops);
        setCustomers(mappedCustomers);
      })
      .catch(err => {
        if (!mounted) return;
        setError(getApiError(err));
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => { mounted = false; };
  }, []);

  // Customer Invoice Filtering - Improved
  const filterInvoicesByCustomer = (filters) => {
    return invoices.filter(inv => {
      // Customer number filter
      if (filters.customerNumber && !ciMatch(inv.customerNumber, filters.customerNumber)) return false;
      
      // Location filter - check both invoice location and shop location
      if (filters.location) {
        const locationMatch = ciIncludes(inv.location, filters.location) || 
                            ciIncludes(inv.shopLocation, filters.location);
        if (!locationMatch) return false;
      }
      
      // Category filter
      if (filters.category && !ciMatch(inv.category, filters.category)) return false;
      
      // Date filter - improved matching
      if (filters.purchaseDate && !dateMatches(inv.date, filters.purchaseDate)) return false;
      
      return true;
    });
  };

  // Shop Analytics
  const filteredInvoices = filterInvoices(invoices, shopFilters);
  const shopKpis = [
    { label: 'Total Invoices', value: filteredInvoices.length, icon: ReceiptLongIcon },
    { label: 'Total Revenue', value: filteredInvoices.reduce((sum, inv) => sum + (inv.amount || 0), 0), icon: AttachMoneyIcon },
    { label: 'Unique Customers', value: new Set(filteredInvoices.map(inv => inv.customerNumber)).size, icon: PeopleAltIcon },
    { label: 'Active Shops', value: new Set(filteredInvoices.map(inv => inv.shopId)).size, icon: StoreMallDirectoryIcon },
  ];

  // Customer Analytics
  const filteredCustomers = filterCustomers(customers, customerFilters);
  const filteredCustomerInvoices = filterInvoicesByCustomer(customerFilters);
  const customerKpis = [
    { label: 'Total Customers', value: filteredCustomers.length, icon: PeopleAltIcon },
    { label: 'Total Purchases', value: filteredCustomerInvoices.length, icon: ReceiptLongIcon },
    { label: 'Total Spent', value: filteredCustomerInvoices.reduce((sum, inv) => sum + (inv.amount || 0), 0), icon: AttachMoneyIcon },
    { label: 'Categories', value: Array.from(new Set(filteredCustomerInvoices.map(inv => inv.category))).length, icon: CategoryIcon },
  ];

  // Export handlers
  const handleExportShop = () => {
    exportToExcel(filteredInvoices, 'shop_invoices.xlsx');
  };
  const handleExportCustomer = () => {
    exportToExcel(filteredCustomerInvoices, 'customer_purchases.xlsx');
  };

  // Clear Filters handlers
  const handleClearShopFilters = () => setShopFilters({});
  const handleClearCustomerFilters = () => setCustomerFilters({});

  if (loading) {
    return <Box p={3} textAlign="center"><CircularProgress /></Box>;
  }
  if (error) {
    return <Box p={3}><Alert severity="error">{error}</Alert></Box>;
  }

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, bgcolor: 'background.default', minHeight: '100vh' }}>
      <Paper elevation={0} sx={{ p: { xs: 2, md: 4 }, borderRadius: 4, boxShadow: 'none', border: '1px solid', borderColor: 'divider', mb: 4, maxWidth: 1100, mx: 'auto' }}>
        <Typography variant="h4" gutterBottom fontWeight={700} color="text.primary">
          Analytics
        </Typography>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} aria-label="analytics tabs" textColor="primary" indicatorColor="primary">
          <Tab label="Shop Analytics" />
          <Tab label="Customer Analytics" />
        </Tabs>
        {/* Shop Analytics Tab */}
        <TabPanel value={tab} index={0}>
          <SectionHeader>Shop Filters</SectionHeader>
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }} elevation={0}>
            <ShopFilter filters={shopFilters} onChange={setShopFilters} shops={shops} invoices={invoices} />
          </Paper>
          <SectionHeader>Shop KPIs</SectionHeader>
          <Grid container spacing={2} mb={2}>
            {/* Only show relevant KPIs for filtered data */}
            <Grid item xs={12} sm={4} md={3}>
              <InvoiceStatsCard label="Total Purchases" stats={{ allTime: filteredInvoices.length }} icon={ReceiptLongIcon} />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <InvoiceStatsCard label="Total Spent" stats={{ allTime: filteredInvoices.reduce((sum, inv) => sum + (inv.amount || 0), 0) }} icon={AttachMoneyIcon} />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <InvoiceStatsCard label="Categories" stats={{ allTime: Array.from(new Set(filteredInvoices.map(inv => inv.category))).length }} icon={CategoryIcon} />
            </Grid>
          </Grid>
          <SectionHeader>Invoices</SectionHeader>
          <Stack direction="row" justifyContent="flex-end" mb={1}>
            <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleExportShop} size="small">
              Export
            </Button>
          </Stack>
          {filteredInvoices.length === 0 ? (
            <Alert severity="info">No results found for the selected filters.</Alert>
          ) : (
            <InvoiceTable rows={filteredInvoices} shops={shops} />
          )}
        </TabPanel>
        {/* Customer Analytics Tab */}
        <TabPanel value={tab} index={1}>
          <SectionHeader>Customer Filters</SectionHeader>
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }} elevation={0}>
            <CustomerFilter filters={customerFilters} onChange={setCustomerFilters} customers={customers} invoices={invoices} />
          </Paper>
          <SectionHeader>Customer KPIs</SectionHeader>
          <Grid container spacing={2} mb={2}>
            {/* Only show relevant KPIs for filtered data */}
            <Grid item xs={12} sm={4} md={3}>
              <InvoiceStatsCard label="Total Purchases" stats={{ allTime: filteredCustomerInvoices.length }} icon={ReceiptLongIcon} />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <InvoiceStatsCard label="Total Spent" stats={{ allTime: filteredCustomerInvoices.reduce((sum, inv) => sum + (inv.amount || 0), 0) }} icon={AttachMoneyIcon} />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <InvoiceStatsCard label="Categories" stats={{ allTime: Array.from(new Set(filteredCustomerInvoices.map(inv => inv.category))).length }} icon={CategoryIcon} />
            </Grid>
          </Grid>
          <SectionHeader>Purchases</SectionHeader>
          <Stack direction="row" justifyContent="flex-end" mb={1}>
            <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleExportCustomer} size="small">
              Export
            </Button>
          </Stack>
          {filteredCustomerInvoices.length === 0 ? (
            <Alert severity="info">No results found for the selected filters.</Alert>
          ) : (
            <InvoiceTable rows={filteredCustomerInvoices} shops={shops} />
          )}
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default Analytics; 
